// Contract-WidgetEmailOTP.md — the way back into the OTP dialog.
//
// Dismissing `EmailVerificationModal` used to be a dead end. Nothing BREAKS —
// the gate lives server-side inside the tool, so an unverified visitor simply
// keeps getting no order data — but the only route back to the dialog was to
// ask an order question again and hope the gate tripped a second time. To the
// visitor that reads as the widget having forgotten what they were doing.
//
// 🚨 Not a security control and not a gate. Verification state is Redis-side
// under `otp:verified:{session_id}`; this bar only reopens a dialog.
//
// Styling deliberately mirrors the handoff-rejection notice strip in
// Widget.tsx: same brand-colour band, same 11px centred row, same border-top —
// it sits in the same slot above the input and a second visual language there
// would read as a different kind of thing.
//
// Responsive: the row wraps at 375px rather than truncating, so the label and
// the "Verify" affordance both stay readable. Contrast comes from the caller's
// `textColor`, which is already computed against `brandColor`.

import React from 'react';
import { ShieldCheck } from 'lucide-react';

interface OtpVerifyBarProps {
  brandColor: string;
  /** Pre-computed for contrast against `brandColor` by the caller. */
  textColor: string;
  isDark: boolean;
  onVerify: () => void;
  /** 🚨 `different_email` means the visitor IS verified and the order is under
   *  some other address. The bar must still appear in that case — it is the
   *  only way back to the dialog, and it is the case with the worst dead end,
   *  since re-confirming the address they already hold can never help. The
   *  label changes because "verify your email" reads as already-done to
   *  someone who just did it. */
  reason?: 'order_lookup' | 'different_email';
}

const OtpVerifyBar: React.FC<OtpVerifyBarProps> = ({
  brandColor,
  textColor,
  isDark,
  onVerify,
  reason = 'order_lookup',
}) => (
  <div
    className={`px-3 pt-2 pb-1 border-t ${
      isDark ? 'border-gray-800' : 'border-gray-200'
    }`}
    style={{ backgroundColor: brandColor }}
  >
    <button
      type="button"
      onClick={onVerify}
      className="w-full flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 text-[11px] font-medium leading-snug text-center py-1"
      style={{ color: textColor }}
    >
      <ShieldCheck size={12} className="flex-shrink-0" />
      <span>
        {reason === 'different_email'
          ? 'That order is under a different email'
          : 'Verify your email to see your order details'}
      </span>
      <span className="underline">
        {reason === 'different_email' ? 'Try another' : 'Verify'}
      </span>
    </button>
  </div>
);

export default OtpVerifyBar;
